import { AlignmentMetadata } from '../hooks/use-common-aligned-items';

import {
	ImageToRulerLengthAllocation,
	ImageToRulerLengthAllocations,
	rulerIsUnallocated,
	RulerSegment,
} from './image-to-ruler-allocation';

/**
 * Converts a list of length allocations into ruler segments to be consumed by a Ruler Lane Row component
 *
 * @param {ImageToRulerLengthAllocations | undefined} allocations - The length allocations to be transformed into ruler segments.
 * @returns {RulerSegment[] | undefined} An array of `RulerSegment` objects created based on the given allocations.
 */
export const rulerAllocationsToRulerSegments = (
	allocations: ImageToRulerLengthAllocations | undefined,
): RulerSegment[] | undefined => {
	return allocations?.map<RulerSegment>((allocation: ImageToRulerLengthAllocation) => {
		// If no ruler range, we have a crop
		if (rulerIsUnallocated(allocation))
			return {
				...allocation,
				label: undefined,
				colour: '#888888',
			};
		// Otherwise, we have an allocated ruler with ticks shown
		return {
			renderableDepth: allocation.renderableDepth,
			// Always fall back to tray if no DH is provided, as to have a 1:1 mapped ruler
			rulerDepth: allocation.rulerDepth,
		};
	});
};

/**
 * Based on a set of image crops and range (via metadata) generate a list of allocations
 * @param alignmentMetadata the Metadata of the image alignment
 * @returns the Allocations generated from image metadata
 */
export const cropsToRulerAllocations = (
	alignmentMetadata: AlignmentMetadata | undefined,
): ImageToRulerLengthAllocations | undefined => {
	if (!alignmentMetadata || !alignmentMetadata.range) return undefined;

	// When the metadata has no crops (i.e DH only image) then return a single whole allocation
	return (alignmentMetadata.crops ?? []).reduce<ImageToRulerLengthAllocations>(
		// To build up ruler segments from crops, we take each crop and generate the ruler before it, and the crop it covers
		(prev, curr) => {
			const precedingSegments = [...prev];
			const lastSegment = precedingSegments.pop();
			// If there was no downhole range in the previous, then it was not a ruler
			if (!lastSegment?.rulerDepth) return prev;
			const precedingSegmentLength = curr.renderableDepth.depthFrom - lastSegment.renderableDepth.depthFrom;
			return [
				...precedingSegments,
				// New trimmed previous ruler segment
				{
					...lastSegment,
					rulerDepth: {
						...lastSegment.rulerDepth,
						depthTo: lastSegment.rulerDepth.depthFrom + precedingSegmentLength,
					},
					renderableDepth: {
						...lastSegment.renderableDepth,
						depthTo: curr.renderableDepth.depthFrom,
					},
				},
				// New crop segment
				{
					renderableDepth: curr.renderableDepth,
					rulerDepth: { depthFrom: curr.rulerAnchor, depthTo: curr.rulerAnchor },
				},
				// New remainder ruler
				{
					renderableDepth: {
						depthFrom: curr.renderableDepth.depthTo,
						depthTo: lastSegment.renderableDepth.depthTo,
					},
					rulerDepth: {
						depthFrom: curr.rulerAnchor,
						depthTo: lastSegment.rulerDepth.depthTo,
					},
				},
			];
		},
		[
			{
				renderableDepth: {
					depthFrom: alignmentMetadata.range.depthFrom,
					depthTo: alignmentMetadata.range.trayTo ?? alignmentMetadata.range.depthTo,
				},
				rulerDepth: { depthFrom: alignmentMetadata.range.depthFrom, depthTo: alignmentMetadata.range.depthTo },
			},
		],
	);
};
